import { supabase, formatSupabaseError } from '../../supabase';
import { stampCreatedAt } from '../../utils/dbTimestamps';
import { createOwnerPanelUserAuth, createAdminPanelUserAuth } from '../auth/authApi';
import { formatDate } from './ownerUtils';

function mapPanelUser(row) {
  return {
    id: row.id,
    authUserId: row.auth_user_id || null,
    gymId: row.gym_id || null,
    name: row.full_name,
    email: row.email,
    phone: row.phone || '',
    role: row.role,
    status: row.status === 'inactive' || row.status === 'suspended' ? 'suspended' : 'active',
    joinedDate: formatDate(row.joined_date || row.created_at),
    lastLogin: row.last_login ? formatDate(row.last_login) : '—',
  };
}

/** Owner users: only owners. Admin users: only gym admins for their own gym. */
async function assertCanManagePanel(panel) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) {
    throw new Error('You must be signed in to manage users.');
  }

  const { data: account, error } = await supabase
    .from('user_accounts')
    .select('id, role, gym_id, status')
    .eq('auth_user_id', session.user.id)
    .eq('status', 'active')
    .maybeSingle();

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not verify your account.'));
  }
  if (!account) {
    throw new Error('No active account found for this login.');
  }

  if (panel === 'owner') {
    if (account.role !== 'owner') {
      throw new Error('Only owner panel users can add or manage owner panel users.');
    }
    return { account, gymId: null };
  }

  if (panel === 'admin') {
    if (account.role !== 'admin') {
      throw new Error('Only gym admins can add or manage users for their gym admin panel.');
    }
    if (!account.gym_id) {
      throw new Error('Your admin account is not linked to a gym. Sign out and sign in again.');
    }
    return { account, gymId: account.gym_id };
  }

  throw new Error('Unknown panel.');
}

export async function fetchPanelUsers(panel = 'owner') {
  const { gymId } = await assertCanManagePanel(panel);

  let query = supabase
    .from('panel_users')
    .select('*')
    .eq('panel', panel)
    .order('created_at', { ascending: false });

  // Admin panel users are scoped to the current gym only
  if (panel === 'admin') {
    query = query.eq('gym_id', gymId);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not load panel users.'));
  }
  return (data || []).map(mapPanelUser);
}

export async function createPanelUser(panel, form) {
  const email = form.email?.trim();
  if (!email) throw new Error('Email is required.');

  const { gymId: scopedGymId } = await assertCanManagePanel(panel);

  let authUserId = null;
  let gymId = null;

  if (panel === 'owner') {
    authUserId = await createOwnerPanelUserAuth({
      email,
      password: form.password,
      fullName: form.name,
    });
  } else if (panel === 'admin') {
    gymId = scopedGymId;
    authUserId = await createAdminPanelUserAuth({
      email,
      password: form.password,
      fullName: form.name,
      gymId,
    });
  }

  const row = {
    panel,
    gym_id: panel === 'admin' ? gymId : null,
    auth_user_id: authUserId,
    full_name: form.name.trim(),
    email,
    phone: form.phone?.trim() || null,
    role: form.role || 'Staff',
    status: form.status === 'suspended' ? 'suspended' : 'active',
    joined_date: new Date().toISOString().slice(0, 10),
  };

  const { data, error } = await supabase.from('panel_users').insert(stampCreatedAt(row)).select('*').single();
  if (error) {
    if (authUserId) {
      await supabase.from('admins').delete().eq('auth_user_id', authUserId);
      await supabase.from('user_accounts').delete().eq('auth_user_id', authUserId);
    }
    throw new Error(formatSupabaseError(error, 'Could not add panel user.'));
  }

  if (authUserId && form.status === 'suspended') {
    await supabase.from('user_accounts').update({ status: 'suspended' }).eq('auth_user_id', authUserId);
    if (panel === 'admin') {
      await supabase.from('admins').update({ status: 'suspended' }).eq('auth_user_id', authUserId);
    }
  }

  return mapPanelUser(data);
}

export async function updatePanelUser(id, form) {
  const { data: existing, error: lookupError } = await supabase
    .from('panel_users')
    .select('id, panel, gym_id, auth_user_id')
    .eq('id', id)
    .maybeSingle();

  if (lookupError) {
    throw new Error(formatSupabaseError(lookupError, 'Could not load user.'));
  }
  if (!existing) throw new Error('User not found.');

  const { gymId } = await assertCanManagePanel(existing.panel);
  if (existing.panel === 'admin' && existing.gym_id !== gymId) {
    throw new Error('You can only manage users for your own gym.');
  }

  const row = {
    full_name: form.name.trim(),
    email: form.email.trim(),
    phone: form.phone?.trim() || null,
    status: form.status === 'suspended' ? 'suspended' : 'active',
  };

  if (form.role) row.role = form.role;

  const { data, error } = await supabase.from('panel_users').update(row).eq('id', id).select('*').single();
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not update panel user.'));
  }

  if (data?.auth_user_id) {
    await supabase
      .from('user_accounts')
      .update({
        full_name: row.full_name,
        email: row.email,
        status: row.status === 'suspended' ? 'suspended' : 'active',
      })
      .eq('auth_user_id', data.auth_user_id);

    if (data.panel === 'admin') {
      await supabase
        .from('admins')
        .update({
          full_name: row.full_name,
          email: row.email,
          status: row.status === 'suspended' ? 'suspended' : 'active',
        })
        .eq('auth_user_id', data.auth_user_id);
    }
  }

  return mapPanelUser(data);
}

export async function deletePanelUser(id) {
  const { data: existing } = await supabase
    .from('panel_users')
    .select('id, auth_user_id, email, panel, gym_id')
    .eq('id', id)
    .maybeSingle();

  if (!existing) throw new Error('User not found.');

  const { gymId } = await assertCanManagePanel(existing.panel);
  if (existing.panel === 'admin' && existing.gym_id !== gymId) {
    throw new Error('You can only manage users for your own gym.');
  }

  const { error } = await supabase.from('panel_users').delete().eq('id', id);
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not delete panel user.'));
  }

  if (existing?.auth_user_id) {
    if (existing.panel === 'admin') {
      await supabase.from('admins').delete().eq('auth_user_id', existing.auth_user_id);
    }
    await supabase.from('user_accounts').delete().eq('auth_user_id', existing.auth_user_id);
  } else if (existing?.email) {
    const role = existing.panel === 'admin' ? 'admin' : 'owner';
    let q = supabase.from('user_accounts').delete().ilike('email', existing.email).eq('role', role);
    if (existing.gym_id) q = q.eq('gym_id', existing.gym_id);
    await q;
  }
}

export async function updatePanelUserStatus(id, status) {
  const { data: existing, error: lookupError } = await supabase
    .from('panel_users')
    .select('id, panel, gym_id, auth_user_id')
    .eq('id', id)
    .maybeSingle();

  if (lookupError) {
    throw new Error(formatSupabaseError(lookupError, 'Could not load user.'));
  }
  if (!existing) throw new Error('User not found.');

  const { gymId } = await assertCanManagePanel(existing.panel);
  if (existing.panel === 'admin' && existing.gym_id !== gymId) {
    throw new Error('You can only manage users for your own gym.');
  }

  const dbStatus = status === 'suspended' ? 'suspended' : 'active';
  const { data, error } = await supabase
    .from('panel_users')
    .update({ status: dbStatus })
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not update status.'));
  }

  if (data?.auth_user_id) {
    await supabase
      .from('user_accounts')
      .update({ status: dbStatus })
      .eq('auth_user_id', data.auth_user_id);

    if (data.panel === 'admin') {
      await supabase
        .from('admins')
        .update({ status: dbStatus })
        .eq('auth_user_id', data.auth_user_id);
    }
  }

  return mapPanelUser(data);
}

export async function sendPanelUserPasswordReset(email, panel = 'owner') {
  await assertCanManagePanel(panel);

  const trimmed = email?.trim();
  if (!trimmed) throw new Error('Email is required.');

  const redirectTo = panel === 'admin'
    ? `${window.location.origin}/`
    : `${window.location.origin}/owner`;

  const { error } = await supabase.auth.resetPasswordForEmail(trimmed, { redirectTo });

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not send password reset email.'));
  }
}
